import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { CreditCard, ExternalLink, AlertTriangle, Crown, Shield, Calendar, XCircle } from "lucide-react";
import CancelSubscriptionDialog from "@/components/subscription/CancelSubscriptionDialog";
import { getPriceId, formatPrice, getPrice } from "@/config/stripe";
import { openExternalUrl } from "@/utils/nativePlugins";

// LIVE MODE Product IDs
const PRODUCT_INFO = {
  'prod_TDc4VzZ2qshOsN': { name: 'Investor Access Monthly', price: '$9.99/month' },
  'prod_TDdbzZM8mwxupk': { name: 'Investor Access Yearly', price: '$99.99/year' },
  'prod_TDc4H4sIqTlOJM': { name: 'Shark Plan Monthly', price: '$34.99/month' },
  'prod_TDc4LIUHwCkuea': { name: 'Shark Plan Yearly', price: '$349.99/year' },
  'prod_VSd9RAkBproawz': { name: 'Shark Plan Monthly', price: '$34.99/month' },
  'prod_VSdACnJTrTZ9Zo': { name: 'Shark Plan Yearly', price: '$349.99/year' },
};

const buyerMonthlyPrice = getPrice("buyer", "monthly");
const buyerAnnualPrice = getPrice("buyer", "annual");
const sellerMonthlyPrice = getPrice("seller", "monthly");
const sellerAnnualPrice = getPrice("seller", "annual");

const UPGRADE_OPTIONS = {
  buyer: [
    { priceId: getPriceId('buyer', 'monthly'), name: 'Monthly Plan', price: `${formatPrice(buyerMonthlyPrice.amount)}/month` },
    { priceId: getPriceId('buyer', 'annual'), name: 'Yearly Plan', price: `${formatPrice(buyerAnnualPrice.amount)}/year`, badge: 'Save $20' }
  ],
  seller: [
    { priceId: getPriceId('seller', 'monthly'), name: 'Shark Monthly', price: `${formatPrice(sellerMonthlyPrice.amount)}/month` },
    { priceId: getPriceId('seller', 'annual'), name: 'Shark Yearly', price: `${formatPrice(sellerAnnualPrice.amount)}/year`, badge: 'Save $69.89' }
  ]
};

const ManageSubscriptionPage = () => {
  const navigate = useNavigate();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const { 
    subscribed,
    userType,
    subscriptionStatus, 
    productId, 
    subscriptionEnd,
    isLoading,
    createCheckoutSession,
    openCustomerPortal,
    cancelSubscription
  } = useSubscription();

  const currentProduct = productId ? PRODUCT_INFO[productId as keyof typeof PRODUCT_INFO] : null;

  const handleOpenPortal = async () => {
    try {
      setIsProcessing(true);
      const url = await openCustomerPortal();
      await openExternalUrl(url);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to open customer portal",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpgrade = async (priceId: string) => {
    try {
      setIsProcessing(true);
      const url = await createCheckoutSession(priceId, userType || 'seller');
      await openExternalUrl(url);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to start checkout",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setIsProcessing(true);
      await cancelSubscription();
      setShowCancelDialog(false);
      toast({
        title: "Subscription Cancelled",
        description: subscriptionStatus === 'trial' 
          ? "Your trial has been cancelled. You won't be charged."
          : "Your subscription will remain active until the end of your billing period.",
      });
    } catch (error: any) {
      setShowCancelDialog(false);
      toast({
        title: "Cancellation issue",
        description: (
          <span>
            We're having issues canceling your account. Please{" "}
            <a href="/contact" className="underline font-semibold">contact support</a>.
          </span>
        ),
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = () => {
    switch (subscriptionStatus) {
      case 'active':
        return <Badge className="bg-green-500"><Shield className="w-3 h-3 mr-1" />Active</Badge>;
      case 'trial':
        return <Badge className="bg-blue-500"><Calendar className="w-3 h-3 mr-1" />Free Trial</Badge>;
      case 'cancelled':
        return <Badge className="bg-orange-500"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      case 'sponsored':
        return <Badge className="bg-purple-500"><Crown className="w-3 h-3 mr-1" />Sponsored Access</Badge>;
      case 'expired':
        return <Badge className="bg-red-500"><AlertTriangle className="w-3 h-3 mr-1" />Expired</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="container max-w-4xl mx-auto py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const canCancelSubscription = subscriptionStatus === 'trial' || subscriptionStatus === 'active';
  const showUpgradeOptions = subscriptionStatus === 'trial' || subscriptionStatus === 'expired' || subscriptionStatus === 'cancelled';
  const canOpenPortal = subscriptionStatus === 'active' || subscriptionStatus === 'cancelled';

  return (
    <div className="container max-w-4xl mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Manage Subscription</h1>
          <p className="text-muted-foreground">View and manage your subscription settings</p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>

      {/* Current Plan Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Current Plan
              </CardTitle>
              <CardDescription className="mt-2">
                {subscriptionStatus === 'sponsored' 
                  ? 'Admin-granted sponsored access' 
                  : (currentProduct?.name || 'No active subscription')}
              </CardDescription>
            </div>
            {getStatusBadge()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4">
            <div className="flex justify-between items-center p-4 border rounded-lg bg-muted/50">
              <div>
                <p className="font-medium text-lg">
                  {subscriptionStatus === 'sponsored' ? 'Complimentary' : (currentProduct?.price || 'Free')}
                </p>
                {subscriptionEnd && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {subscriptionStatus === 'trial' && 'Trial ends'}
                    {subscriptionStatus === 'active' && 'Renews'}
                    {subscriptionStatus === 'cancelled' && 'Access until'}
                    {' on '}{format(new Date(subscriptionEnd), 'PPP')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Trial Warning */}
          {subscriptionStatus === 'trial' && subscriptionEnd && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-2">
                <Calendar className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Free Trial Active</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Your trial expires in {Math.ceil((new Date(subscriptionEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} days.
                    Upgrade now to continue accessing all features without interruption.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Sponsored Info */}
          {subscriptionStatus === 'sponsored' && (
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex gap-2">
                <Crown className="w-5 h-5 text-purple-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-purple-900">Sponsored Access</p>
                  <p className="text-sm text-purple-700 mt-1">
                    Your access is managed by an administrator. No billing or payment is required.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Cancelled Warning */}
          {subscriptionStatus === 'cancelled' && subscriptionEnd && (
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-orange-900">Subscription Cancelled</p>
                  <p className="text-sm text-orange-700 mt-1">
                    You'll have access until {format(new Date(subscriptionEnd), 'PPP')}. 
                    Reactivate anytime to continue without losing your data.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Management Card */}
      {canOpenPortal && (
        <Card>
          <CardHeader>
            <CardTitle>Payment & Billing</CardTitle>
            <CardDescription>
              Manage your payment methods and view billing history
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleOpenPortal}
              disabled={isProcessing}
              className="w-full"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Customer Portal
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Opens Stripe portal where you can update payment methods, download invoices, and view billing history.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Cancel Subscription Card */}
      {canCancelSubscription && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600">Cancel Subscription</CardTitle>
            <CardDescription>
              {subscriptionStatus === 'trial' 
                ? "Cancel your trial to prevent future charges"
                : "Cancel your subscription at the end of your billing period"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              variant="destructive"
              onClick={() => setShowCancelDialog(true)}
              disabled={isProcessing}
              className="w-full"
            >
              <XCircle className="w-4 h-4 mr-2" />
              {subscriptionStatus === 'trial' ? 'Cancel Trial' : 'Cancel Subscription'}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              {subscriptionStatus === 'trial'
                ? "You won't be charged. Access continues until trial ends."
                : "You'll keep access until the end of your billing period."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Upgrade Options Card */}
      {showUpgradeOptions && userType === 'seller' && (
        <Card className="border-[#ffd700]/30">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-[#ffd700]/10 flex items-center justify-center">
                <Crown className="w-6 h-6 text-[#ffd700]" />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">
                  {subscriptionStatus === 'cancelled' ? 'Reactivate Your Shark Plan' : 'Upgrade to Shark Plan'}
                </h3>
                <p className="text-muted-foreground max-w-md">
                  Unlock unlimited listings, advanced buyer search, direct buyer contact, and premium features to scale your real estate business.
                </p>
              </div>
              <Button 
                onClick={() => navigate('/seller/upgrade')}
                size="lg"
                className="w-full max-w-sm bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200] font-semibold"
              >
                {subscriptionStatus === 'cancelled' ? 'Reactivate Now' : 'View Upgrade Options'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Buyer Upgrade Options */}
      {showUpgradeOptions && userType === 'buyer' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-500" />
              {subscriptionStatus === 'cancelled' ? 'Reactivate Subscription' : 'Upgrade Your Plan'}
            </CardTitle>
            <CardDescription>
              {subscriptionStatus === 'cancelled' 
                ? 'Choose a plan to reactivate your subscription'
                : 'Choose the plan that works best for you'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {UPGRADE_OPTIONS[userType].map((option) => (
                <div key={option.priceId} className="flex items-center justify-between p-4 border rounded-lg hover:border-primary transition-colors">
                  <div>
                    <p className="font-medium">{option.name}</p>
                    <p className="text-sm text-muted-foreground">{option.price}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {option.badge && (
                      <Badge variant="secondary" className="text-xs">
                        {option.badge}
                      </Badge>
                    )}
                    <Button 
                      onClick={() => handleUpgrade(option.priceId)}
                      disabled={isProcessing}
                      className="bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200]"
                    >
                      {subscriptionStatus === 'cancelled' ? 'Reactivate' : 'Upgrade'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <CancelSubscriptionDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onConfirm={handleCancelSubscription}
        isTrial={subscriptionStatus === 'trial'}
        endDate={subscriptionEnd ? format(new Date(subscriptionEnd), 'PPP') : ''}
        isProcessing={isProcessing}
      />
    </div>
  );
};

export default ManageSubscriptionPage;
